import React, { useEffect, useState } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { Users, AlertTriangle, Car, DollarSign, Activity, Paperclip, BarChart2, CheckCircle } from 'lucide-react';
import { useServices, useToast } from '@/app/providers';
import { isSectionAdmin, useAbility } from '@/modules/identity/application/ability';
import { useDateFormatter } from '@/shared/hooks';
import { useI18n } from '@/shared/i18n';
import { Announcement, UserRole } from '@/shared/types';
import { GuardDashboardPage } from '@/modules/guard';

const data = [
  { name: 'Lun', accesos: 40, visitas: 24 },
  { name: 'Mar', accesos: 30, visitas: 13 },
  { name: 'Mie', accesos: 20, visitas: 58 },
  { name: 'Jue', accesos: 27, visitas: 39 },
  { name: 'Vie', accesos: 18, visitas: 48 },
  { name: 'Sab', accesos: 63, visitas: 88 },
  { name: 'Dom', accesos: 34, visitas: 43 },
];

const StatCard = ({ title, value, icon: Icon, color, trend }: any) => (
  <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-100 flex items-start justify-between">
    <div>
      <p className="text-slate-500 text-sm font-medium mb-1">{title}</p>
      <h3 className="text-2xl font-bold text-slate-800">{value}</h3>
      {trend && <p className="text-xs text-green-600 mt-2 font-medium">{trend}</p>}
    </div>
    <div className={`p-3 rounded-lg ${color}`}>
      <Icon className="w-6 h-6 text-white" />
    </div>
  </div>
);

export const Dashboard = () => {
  const { t } = useI18n();
  const { formatDate } = useDateFormatter();
  const toast = useToast();
  const ability = useAbility();
  const { announcementRepo, userRepo } = useServices();
  const [announcements, setAnnouncements] = useState<Announcement[]>([]);
  const [currentUser, setCurrentUser] = useState<any>(null);

  useEffect(() => {
    const init = async () => {
      const user = await userRepo.getCurrentUser();
      setCurrentUser(user);
      fetchAnnouncements();
    };
    init();
  }, [userRepo]);

  const fetchAnnouncements = async () => {
    const data = await announcementRepo.getAllAnnouncements();
    setAnnouncements(data);
  };

  const handleVote = async (annId: string, optId: string) => {
    if (!currentUser) return;
    try {
      await announcementRepo.voteInPoll(annId, optId, currentUser.id);
      await fetchAnnouncements(); // Refresh to show results
    } catch (e) {
      toast.warning('Ya has votado o hubo un error.');
    }
  };

  const getPriorityStyles = (type: string) => {
    switch (type) {
      case 'URGENT': return 'border-red-500 bg-red-50 text-red-700';
      case 'IMPORTANT': return 'border-amber-500 bg-amber-50 text-amber-700';
      case 'EVENT': return 'border-emerald-500 bg-emerald-50 text-emerald-700';
      default: return 'border-blue-500 bg-blue-50 text-blue-700';
    }
  };

  if (!currentUser) return null;

  // Nota: no se compara currentUser.tenantRole (string libre, editable por el tenant admin
  // al crear roles custom) para decidir el layout — solo el rol maestro verificado y las
  // habilidades CASL reales, que ya cubren a los guardias legítimos.
  const hasGuardRole = currentUser.role === UserRole.GUARD;
  const hasGuardAbility = ability.can('read', 'GuardPanel')
    || ability.can('read', 'AccessLog')
    || ability.can('read', 'Shift')
    || ability.can('create', 'Shift');
  // SECTION_ADMIN lee AccessLog como admin, no como guardia.
  const isGuard = (hasGuardRole || hasGuardAbility) && !isSectionAdmin(currentUser);

  // Guards see the dedicated GuardDashboardPage
  if (isGuard) {
    return <GuardDashboardPage />;
  }

  const statCards = [
    { key: 'visits', title: 'Visitas Activas', value: '12', icon: Users, color: 'bg-blue-500', trend: '+4 vs ayer', canView: ability.can('read', 'Guest') },
    { key: 'payments', title: 'Pagos Pendientes', value: '$52,400', icon: DollarSign, color: 'bg-emerald-500', trend: '85% Recaudado', canView: ability.can('read', 'Payment') },
    { key: 'vehicles', title: 'Vehículos en Recinto', value: '145', icon: Car, color: 'bg-indigo-500', canView: ability.can('read', 'Vehicle') },
    { key: 'tickets', title: 'Reportes Abiertos', value: '3', icon: AlertTriangle, color: 'bg-amber-500', trend: '-2 vs semana pasada', canView: ability.can('read', 'Ticket') },
  ].filter((card) => card.canView);

  const showChart = ability.can('read', 'AccessLog');
  const showNotices = ability.can('read', 'Announcement');
  const hasAnyWidget = statCards.length > 0 || showChart || showNotices;

  const statsGridColsClass = statCards.length >= 4
    ? 'lg:grid-cols-4'
    : statCards.length === 3
    ? 'lg:grid-cols-3'
    : statCards.length === 2
    ? 'lg:grid-cols-2'
    : 'lg:grid-cols-1';

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-slate-800">Resumen General</h2>
        <span className="text-sm text-slate-500">Última actualización: hace 5 min</span>
      </div>

      {!hasAnyWidget && (
        <div className="bg-white p-8 rounded-xl shadow-sm border border-slate-100 text-center text-slate-500 text-sm">
          No tienes permisos asignados para ver contenido en este panel.
        </div>
      )}

      {/* Stats Grid */}
      {statCards.length > 0 && (
        <div className={`grid grid-cols-1 md:grid-cols-2 ${statsGridColsClass} gap-6`}>
          {statCards.map((card) => (
            <StatCard key={card.key} title={card.title} value={card.value} icon={card.icon} color={card.color} trend={card.trend} />
          ))}
        </div>
      )}

      {(showChart || showNotices) && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Chart */}
          {showChart && (
            <div className={`${showNotices ? 'lg:col-span-2' : 'lg:col-span-3'} bg-white p-6 rounded-xl shadow-sm border border-slate-100`}>
              <h3 className="font-semibold text-slate-800 mb-6 flex items-center gap-2">
                <Activity size={20} className="text-primary-500"/>
                Actividad de Accesos (Semanal)
              </h3>
              <div className="h-72 w-full">
                <ResponsiveContainer width="100%" height="100%" minWidth={0}>
                  <BarChart data={data}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                    <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{fill: '#64748b'}} />
                    <YAxis axisLine={false} tickLine={false} tick={{fill: '#64748b'}} />
                    <Tooltip
                      contentStyle={{borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)'}}
                      cursor={{fill: '#f1f5f9'}}
                    />
                    <Bar dataKey="accesos" fill="#0ea5e9" radius={[4, 4, 0, 0]} name="Residentes" />
                    <Bar dataKey="visitas" fill="#94a3b8" radius={[4, 4, 0, 0]} name="Visitas" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}

          {/* Notices */}
          {showNotices && (
            <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-100 h-full flex flex-col">
            <h3 className="font-semibold text-slate-800 mb-4">Avisos Recientes</h3>
            <div className="space-y-4 overflow-y-auto flex-1 custom-scrollbar pr-2 max-h-[500px]">
              {announcements.map((ann) => {
                 const hasVoted = ann.poll?.hasVoted;
                 const totalVotes = ann.poll ? ann.poll.options.reduce((acc, curr) => acc + curr.votes, 0) : 0;

                 return (
                  <div key={ann.id} className={`p-4 rounded-lg border-l-4 ${getPriorityStyles(ann.type)} bg-opacity-30`}>
                    <div className="flex justify-between items-start mb-1">
                      <h4 className="text-sm font-bold opacity-90">{ann.title}</h4>
                      <span className="text-[10px] uppercase font-bold tracking-wider opacity-70 border border-current px-1 rounded">{t(`admin.ann.priority.${ann.type.toLowerCase()}`)}</span>
                    </div>
                    
                    <p className="text-sm opacity-80 leading-relaxed mb-3 text-slate-700">{ann.content}</p>

                    {/* Attachment */}
                    {ann.attachmentUrl && (
                      <a 
                        href={ann.attachmentUrl} 
                        target="_blank" 
                        rel="noreferrer" 
                        className="inline-flex items-center gap-2 text-xs font-bold bg-white/50 px-3 py-2 rounded-lg hover:bg-white transition-colors mb-3 text-slate-700"
                      >
                        <Paperclip size={14}/> {t('admin.ann.attach_view')}
                      </a>
                    )}

                    {/* Poll Section */}
                    {ann.poll && (
                      <div className="bg-white/60 p-3 rounded-lg border border-slate-200/50">
                        <p className="text-xs font-bold text-slate-700 mb-2 flex items-center gap-1">
                          <BarChart2 size={12}/> {ann.poll.question}
                        </p>
                        
                        <div className="space-y-2">
                          {ann.poll.options.map(opt => {
                            const percentage = totalVotes > 0 ? Math.round((opt.votes / totalVotes) * 100) : 0;
                            
                            if (hasVoted) {
                              return (
                                <div key={opt.id} className="relative h-8 bg-slate-100 rounded overflow-hidden">
                                  <div className="absolute top-0 left-0 h-full bg-primary-200 transition-all duration-500" style={{ width: `${percentage}%` }} />
                                  <div className="absolute inset-0 flex items-center justify-between px-3 text-xs font-medium text-slate-700">
                                    <span>{opt.text}</span>
                                    <span>{percentage}% ({opt.votes})</span>
                                  </div>
                                </div>
                              );
                            }

                            return (
                              <button 
                                key={opt.id}
                                onClick={() => handleVote(ann.id, opt.id)}
                                className="w-full text-left px-3 py-1.5 text-xs bg-white border border-slate-200 rounded hover:bg-primary-50 hover:border-primary-200 hover:text-primary-700 transition-colors"
                              >
                                {opt.text}
                              </button>
                            );
                          })}
                        </div>
                        {hasVoted && (
                          <p className="text-[10px] text-center mt-2 text-emerald-600 font-bold flex items-center justify-center gap-1">
                            <CheckCircle size={10}/> Ya votaste
                          </p>
                        )}
                      </div>
                    )}

                    <p className="text-[10px] opacity-60 mt-2 text-right">{formatDate(ann.date)}</p>
                  </div>
                 );
              })}
            </div>
          </div>
          )}
        </div>
      )}
    </div>
  );
};
